import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardAPI, callsAPI, upsellAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
  Phone, ShoppingBag, TrendingUp, TrendingDown, Clock, DollarSign,
  ArrowRight, PhoneIncoming, Loader2, ArrowUpRight, Brain, Sparkles,
  AlertTriangle, CheckCircle2, Lightbulb, Radio
} from "lucide-react";

const OUTCOME_STYLES = {
  order_placed: { label: "Order Placed", className: "bg-emerald-50 text-emerald-700 border border-emerald-200 badge-pill" },
  transferred: { label: "Transferred", className: "bg-amber-50 text-amber-700 border border-amber-200 badge-pill" },
  abandoned: { label: "Abandoned", className: "bg-red-50 text-red-600 border border-red-200 badge-pill" },
};

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatTime(isoString) {
  return new Date(isoString).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

// Mini sparkline SVG component
function Sparkline({ data, color, height = 28, width = 72 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const points = data.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`).join(" ");
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <polyline points={areaPoints} fill={color} fillOpacity="0.08" stroke="none" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dot on last point */}
      <circle cx={width} cy={parseFloat(points.split(" ").pop().split(",")[1])} r="2.5" fill={color} />
    </svg>
  );
}

// Trend badge component
function TrendBadge({ value }) {
  if (value === 0 || value === undefined || value === null) {
    return <span className="text-[10px] text-slate-400 font-medium">--</span>;
  }
  const isPositive = value > 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${
      isPositive ? "text-emerald-600" : "text-red-500"
    }`}>
      <Icon className="w-3 h-3" />
      {isPositive ? "+" : ""}{value}%
    </span>
  );
}

export default function DashboardPage() {
  const [metrics, setMetrics] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [liveCalls, setLiveCalls] = useState([]);
  const [upsellMetrics, setUpsellMetrics] = useState(null);
  const { isPremium } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [metricsRes, callsRes, trendsRes, liveRes] = await Promise.all([
          dashboardAPI.metrics(),
          dashboardAPI.recentCalls(),
          dashboardAPI.weeklyTrends(),
          callsAPI.live(),
        ]);
        setMetrics(metricsRes.data);
        setRecentCalls(callsRes.data);
        setTrends(trendsRes.data);
        setLiveCalls(liveRes.data || []);

        // Fetch upsell metrics (non-blocking, premium only)
        if (true) { // always try, backend will 403 if not premium
          try {
            const uRes = await upsellAPI.getMetrics();
            setUpsellMetrics(uRes.data);
          } catch { /* not premium or no data */ }
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const dailyData = trends?.daily || [];
  const changes = trends?.changes || {};

  const metricCards = [
    {
      label: "Calls Today", value: metrics?.calls_today || 0,
      icon: Phone, lightBg: "bg-blue-50", lightText: "text-blue-600",
      sparkColor: "#3b82f6",
      sparkData: dailyData.map(d => d.calls),
      change: changes.calls,
    },
    {
      label: "Orders Today", value: metrics?.orders_today || 0,
      icon: ShoppingBag, lightBg: "bg-emerald-50", lightText: "text-emerald-600",
      sparkColor: "#10b981",
      sparkData: dailyData.map(d => d.orders),
      change: changes.orders,
    },
    {
      label: "Conversion", value: `${metrics?.conversion_rate || 0}%`,
      icon: TrendingUp, lightBg: "bg-violet-50", lightText: "text-violet-600",
      sparkColor: "#8b5cf6",
      sparkData: dailyData.map(d => d.conversion),
      change: changes.conversion,
    },
    {
      label: "Avg Duration", value: formatDuration(metrics?.avg_duration || 0),
      icon: Clock, lightBg: "bg-amber-50", lightText: "text-amber-600",
      sparkColor: "#f59e0b",
      sparkData: dailyData.map(d => d.avg_duration),
      change: changes.avg_duration,
    },
    {
      label: "Revenue", value: `$${(metrics?.revenue_today || 0).toFixed(0)}`,
      icon: DollarSign, lightBg: "bg-teal-50", lightText: "text-teal-600",
      sparkColor: "#14b8a6",
      sparkData: dailyData.map(d => d.revenue),
      change: changes.revenue,
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="fade-in">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dashboard-title">
          Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Here's how your phone agent is performing today.</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4" data-testid="metrics-grid">
        {metricCards.map((m, i) => {
          const Icon = m.icon;
          return (
            <Card key={m.label} className={`metric-card fade-in stagger-${i + 1} border-slate-100 overflow-hidden`} data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between mb-2">
                  <div className={`w-8 h-8 rounded-lg ${m.lightBg} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${m.lightText}`} strokeWidth={2} />
                  </div>
                  <TrendBadge value={m.change} />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                <div className="flex items-end justify-between mt-1">
                  <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{m.label}</p>
                  <Sparkline data={m.sparkData} color={m.sparkColor} height={24} width={56} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Live Call Strip */}
      <div
        className={`fade-in stagger-5 flex items-center justify-between px-4 py-3 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
          liveCalls.length > 0
            ? "bg-red-50/70 border-red-200"
            : "bg-slate-50/70 border-slate-100"
        }`}
        onClick={() => navigate("/calls/live")}
        data-testid="live-call-indicator"
      >
        <div className="flex items-center gap-3">
          {liveCalls.length > 0 ? (
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 pulse-orange" />
          ) : (
            <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
          )}
          <PhoneIncoming className={`w-4 h-4 ${liveCalls.length > 0 ? "text-red-600" : "text-slate-400"}`} strokeWidth={2} />
          <span className={`text-[13px] font-medium ${liveCalls.length > 0 ? "text-red-800" : "text-slate-500"}`}>
            {liveCalls.length > 0
              ? <>Live Calls: <strong>{liveCalls.length}</strong> &mdash; {liveCalls.map(c => c.caller_number).join(", ")}</>
              : "No active calls right now"}
          </span>
        </div>
        <Button variant="ghost" size="sm" className="h-7 text-[11px]" data-testid="view-live-calls">
          View live <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>

      {/* AI Insights — Premium only */}
      {isPremium && (
      <Card className="slide-up border-slate-100" data-testid="ai-insights-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2 px-5 pt-5">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Brain className="w-4 h-4 text-primary" /> AI Insights
          </CardTitle>
          <Button
            variant="outline" size="sm" className="h-8 text-[11px]"
            onClick={async () => { setLoadingInsights(true); try { const r = await dashboardAPI.aiInsights(); setAiInsights(r.data); } catch(e) { console.error(e); } finally { setLoadingInsights(false); } }}
            disabled={loadingInsights}
            data-testid="generate-ai-insights"
          >
            {loadingInsights ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1 text-amber-500" />}
            {aiInsights ? "Refresh" : "Generate"}
          </Button>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          {loadingInsights ? (
            <div className="flex items-center gap-2 py-6 justify-center text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Analyzing your data...
            </div>
          ) : aiInsights?.insights?.length > 0 ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {aiInsights.insights.map((insight, i) => {
                  const isPositive = insight.type === "positive";
                  const isWarning = insight.type === "warning";
                  return (
                    <div key={i} className={`p-3 rounded-xl border ${
                      isPositive ? "border-emerald-100 bg-emerald-50/50" : isWarning ? "border-amber-100 bg-amber-50/50" : "border-blue-100 bg-blue-50/50"
                    }`}>
                      <div className="flex items-start gap-2">
                        {isPositive ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : isWarning ? <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" /> : <Lightbulb className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />}
                        <div>
                          <p className="text-[12px] font-semibold text-slate-700">{insight.title}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{insight.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {aiInsights.recommendation && (
                <div className="p-3 rounded-xl bg-primary/5 border border-primary/10">
                  <p className="text-[10px] text-primary/60 uppercase tracking-widest font-semibold mb-1">Recommendation</p>
                  <p className="text-[12px] text-slate-700 leading-relaxed">{aiInsights.recommendation}</p>
                </div>
              )}
              {(aiInsights.top_item || aiInsights.peak_hour) && (
                <div className="flex gap-4 text-[11px]">
                  {aiInsights.top_item && <span className="text-slate-400">Top item: <strong className="text-slate-600">{aiInsights.top_item}</strong></span>}
                  {aiInsights.peak_hour && <span className="text-slate-400">Peak: <strong className="text-slate-600">{aiInsights.peak_hour}</strong></span>}
                </div>
              )}
            </div>
          ) : (
            <p className="text-[12px] text-slate-400 text-center py-4">Click Generate to get AI-powered insights about your orders and calls</p>
          )}
        </CardContent>
      </Card>
      )}

      {/* Upsell Metrics (Premium) */}
      {isPremium && upsellMetrics && upsellMetrics.total_offered > 0 && (
        <Card className="slide-up border-slate-100" data-testid="upsell-metrics-card">
          <CardHeader className="pb-2 px-5 pt-5">
            <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <TrendingUp className="w-4 h-4 text-orange-500" /> Upsell Performance
              <Badge className="bg-orange-50 text-orange-600 border border-orange-200 badge-pill text-[9px]">Premium</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-3 rounded-xl bg-orange-50/50 border border-orange-100">
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{upsellMetrics.acceptance_rate}%</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Acceptance Rate</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>${upsellMetrics.upsell_revenue.toFixed(2)}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Upsell Revenue</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-50/50 border border-blue-100">
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{upsellMetrics.total_accepted}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Accepted</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{upsellMetrics.total_offered}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Total Offered</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Calls */}
      <Card className="slide-up border-slate-100" data-testid="recent-calls-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2 px-5 pt-5">
          <CardTitle className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Calls</CardTitle>
          <Button variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-primary h-8" onClick={() => navigate("/calls")} data-testid="view-all-calls-button">
            View all <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {recentCalls.length === 0 ? (
            <div className="py-16 text-center" data-testid="no-calls-message">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500 font-medium">No calls yet</p>
              <p className="text-xs text-slate-400 mt-1">Calls will appear here once AI answers inbound calls.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100 hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pl-5">Time</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Caller</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Outcome</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Duration</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Confidence</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold text-right pr-5"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentCalls.slice(0, 7).map((call) => {
                    const outcomeStyle = OUTCOME_STYLES[call.outcome] || { label: call.outcome, className: "bg-gray-50 text-gray-600" };
                    const confidencePercent = (call.confidence * 100).toFixed(0);
                    return (
                      <TableRow
                        key={call.id}
                        className="cursor-pointer table-row-hover border-slate-50"
                        onClick={() => navigate(`/calls/${call.id}`)}
                        data-testid={`call-row-${call.id}`}
                      >
                        <TableCell className="text-[13px] text-slate-600 pl-5 py-3">{formatTime(call.start_time)}</TableCell>
                        <TableCell className="text-[13px] text-slate-700 font-mono tracking-tight">{call.caller_number}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={outcomeStyle.className}>{outcomeStyle.label}</Badge>
                        </TableCell>
                        <TableCell className="text-[13px] text-slate-500 tabular-nums">{formatDuration(call.duration_seconds)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="confidence-bar w-12">
                              <div
                                className="confidence-bar-fill"
                                style={{
                                  width: `${confidencePercent}%`,
                                  background: confidencePercent >= 80 ? '#22c55e' : confidencePercent >= 60 ? '#f59e0b' : '#ef4444'
                                }}
                              />
                            </div>
                            <span className="text-[12px] text-slate-500 tabular-nums">{confidencePercent}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right pr-5">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-primary" data-testid={`view-call-${call.id}`}>
                            <ArrowUpRight className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
